import _ from 'underscore';
import $ from 'jquery';
import projectionGrid from 'projection-grid';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import { a11yPlugin } from '@bingads-webui/projection-grid-a11y';
import { assistedSelectionPlugin } from '@bingads-webui/projection-grid-assisted-selection';
import { crossPageSelectAllPlugin } from '@bingads-webui/projection-grid-cross-page-select-all';
import { editablePlugin } from '@bingads-webui-campaign/projection-grid-editable';
import { toolbarPlugin } from '@bingads-webui/projection-grid-toolbar';
import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { placeholderPlugin } from '@bingads-webui/projection-grid-placeholder';
import { uiBlockerPlugin } from '@bingads-webui/projection-grid-uiblocker';
import { inlineErrorPlugin } from '@bingads-webui/projection-grid-inline-error';
import { columnChooserPlugin } from '@bingads-webui-campaign/projection-grid-column-chooser';
import { perfMarkerPlugin } from '@bingads-webui-campaign/pgrid-performance-log';
import { InlineLocationRecommendationDialog } from '@bingads-webui-campaign/inline-location-recommendation';
import { BudgetSplitView } from '@bingads-webui-campaign/campaignui-common-columns';
import { GridCustomizedPopupEditor, getEditorPositionAsideAnchor } from '@bingads-webui-campaign/grid-customized-popup-editor';

import {
  GridView,
  TargetingSettingsContext,
  TargetingType,
} from '@bingads-webui-campaign/targeting-settings-scaffolds';

import SettingsGridColumnsConfig from './settings-grid-column-config';
import SettingsGridConfig from './settings-grid-config';
import settingsGridTemplate from './settings-grid-template.pug';
import SettingsRepository from './settings-repository';
import { settingsBulkEdit } from './settings-bulk-edit';

export default class SettingsGridView extends DeterministicView {
  initialize({
    dataSource,
    perfMarker,
  }) {
    super.setState({
      dataSource,
      setting: TargetingType.AllSettings,
    });

    this.perfMarker = perfMarker;
    this.buildGrid({ dataSource }).then(() => {
      this.grid.gridView.on('didEdit', this.onItemModified, this);
    });
  }

  get events() {
    return {
      'click .inline-location-target-recommendation': 'showInlineLocationRecommendation',
      'click .experiment-budget-ellipsis': 'showExperimentBudgetPopup',
    };
  }

  showExperimentBudgetPopup(event) {
    const item = this.grid.gridView.itemOfElement(event.target);
    const budgetSplitView = new BudgetSplitView({
      item,
      i18n: TargetingSettingsContext.i18n,
    }).render();

    return new GridCustomizedPopupEditor({
      editorView: budgetSplitView,
      editorPositionFactory: getEditorPositionAsideAnchor($(event.target)),
      renderBelow: false,
      className: 'grid-popupeditor experiment-budget-popup',
    }).render();
  }

  showInlineLocationRecommendation(event) {
    const campaign = this.grid.gridView.itemOfElement(event.target);
    this.inlineLocationRecommendationView = new InlineLocationRecommendationDialog({
      scope: {
        customerId: TargetingSettingsContext.state.get('customer_id'),
        accountId: TargetingSettingsContext.state.get('account_id'),
        campaignId: campaign.Id,
      },
      deps: TargetingSettingsContext,
    }).render();
    this.inlineLocationRecommendationView.on('refresh:recommendations', () => this.forceRefreshGrid()); // when invalid recommendations, force refresh grid
  }

  forceRefreshGrid() {
    TargetingSettingsContext.CampaignCacheManager.invalidate('Campaign');
    this.grid.gridView.refresh(true);
  }

  buildGrid({ dataSource }) {
    let gridFactory = projectionGrid.factory({ vnext: true })
      .use(perfMarkerPlugin)
      .use(a11yPlugin)
      .use(assistedSelectionPlugin)
      .use(toolbarPlugin)
      .use(paginationPlugin)
      .use(crossPageSelectAllPlugin)
      .use(TargetingSettingsContext.filterStatePlugin)
      .use(uiBlockerPlugin)
      .use(placeholderPlugin)
      .use(inlineErrorPlugin)
      .use(columnChooserPlugin)
      .use(TargetingSettingsContext.quickFilterPlugin)
      .use(settingsBulkEdit({
        status: TargetingSettingsContext.campaignStatus,
        ChangeStatusView: TargetingSettingsContext.ChangeStatusView,
        ChangeStatusModel: TargetingSettingsContext.ChangeStatusModel,
        ChangeUrlOptionsView: TargetingSettingsContext.ChangeUrlOptionsView,
        ChangeUrlOptionsModel: TargetingSettingsContext.ChangeUrlOptionsModel,
        i18n: TargetingSettingsContext.i18n,
        config: TargetingSettingsContext.config,
      }));

    if (!TargetingSettingsContext.pageContext.Permissions.IsReadOnlyUser
      && !TargetingSettingsContext.config.get('CurrentAccountInActive')) {
      gridFactory = gridFactory.use(editablePlugin);
    }

    this.repository = _.isUndefined(dataSource)
      ? new SettingsRepository()
      : new dataSource.SettingsRepository();

    const columnConfig = new SettingsGridColumnsConfig({
      view: this,
      permissions: TargetingSettingsContext.pageContext.Permissions,
    });
    const gridConfig = new SettingsGridConfig(
      this,
      columnConfig,
      this.repository,
      this.perfMarker
    );

    return TargetingSettingsContext.fetchPreferenceConfig(gridConfig).then(() => {
      gridConfig.dataSource = _.omit(gridConfig.dataSource, 'query');
      this.grid = gridFactory.create(gridConfig);
      TargetingSettingsContext.logger.logMessage(
        'ClientTrace',
        { name: 'Info', value: 0 },
        'SettingsGridView.buildGrid',
        'Settings grid created.'
      );
      this.grid.gridView.on('willUpdate', (changes) => {
        TargetingSettingsContext.handleSortChange(changes, this.grid.gridView, gridConfig.name);
      });
      this.setChild('settings-grid', new GridView({ grid: this.grid }));
    });
  }

  onItemModified() {
  }

  remove() {
    if (this.inlineLocationRecommendationView) {
      this.inlineLocationRecommendationView.remove();
    }
    if (this.grid) {
      if (this.grid.toolbar && this.grid.toolbar.gridToolbarGroup) {
        this.grid.toolbar.gridToolbarGroup.remove();
        this.grid.toolbar.gridToolbarGroup = null;
      }

      if (this.grid.gridView) {
        this.grid.gridView.trigger('willRemove');
        this.grid.gridView.remove();
        this.grid.gridView = null;
      }
    }
    super.remove();
  }

  get template() { return settingsGridTemplate; }

  get className() { return 'settings-grid-view'; }
}
